/**
 * External MIDI Device Manager Hook
 * Handles Web MIDI API device detection, hot-plug, and message routing
 * Includes WebMIDI.js polyfill for Safari support
 */
import { useState, useEffect, useCallback, useRef } from 'react';

// Initialize WebMIDI polyfill for browsers without native support
let webmidiInitialized = false;
const initWebMIDI = async () => {
  if (webmidiInitialized) return;
  
  // Only load polyfill if native Web MIDI is not supported
  if (typeof navigator !== 'undefined' && !('requestMIDIAccess' in navigator)) {
    try {
      // Dynamic import to avoid loading if not needed
      const { WebMidi } = await import('webmidi');
      await WebMidi.enable({ sysex: false });
    } catch (err) {
    }
  }
  webmidiInitialized = true;
};

// Initialize on module load
initWebMIDI();

// MIDI Message Types
const MIDI_NOTE_OFF = 0x80;
const MIDI_NOTE_ON = 0x90;
const MIDI_CONTROL_CHANGE = 0xB0;
const MIDI_PITCH_BEND = 0xE0;

// Common CC numbers
export const MIDI_CC = {
  MODULATION: 1,
  BREATH: 2,
  VOLUME: 7,
  PAN: 10,
  EXPRESSION: 11,
  SUSTAIN: 64,
  ALL_NOTES_OFF: 123,
};

/**
 * Custom hook for managing external MIDI devices
 * @param {Object} options
 * @param {Function} options.onNoteOn - Callback for note on events (pitch, velocity, channel)
 * @param {Function} options.onNoteOff - Callback for note off events (pitch, velocity, channel)
 * @param {Function} options.onControlChange - Callback for CC events (controller, value, channel)
 * @param {Function} options.onPitchBend - Callback for pitch bend events (value, channel)
 */
export const useMIDIDevices = ({
  onNoteOn,
  onNoteOff,
  onControlChange,
  onPitchBend,
} = {}) => {
  // Check Web MIDI API support (including polyfill)
  const [isSupported, setIsSupported] = useState(() => {
    return typeof navigator !== 'undefined' && 'requestMIDIAccess' in navigator;
  });
  const [hasAccess, setHasAccess] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [error, setError] = useState(null);
  const [inputs, setInputs] = useState([]);
  const [outputs, setOutputs] = useState([]);
  const [selectedInputId, setSelectedInputId] = useState(null);
  const [isEnabled, setIsEnabled] = useState(true);
  
  const midiAccessRef = useRef(null);

  // Re-check support after polyfill loads
  useEffect(() => {
    const checkSupport = () => {
      const supported = typeof navigator !== 'undefined' && 'requestMIDIAccess' in navigator;
      setIsSupported(supported);
    };
    
    // Check immediately and after a delay (polyfill may load async)
    checkSupport();
    const timer = setTimeout(checkSupport, 1000);
    return () => clearTimeout(timer);
  }, []);
  const callbacksRef = useRef({ onNoteOn, onNoteOff, onControlChange, onPitchBend });
  
  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = { onNoteOn, onNoteOff, onControlChange, onPitchBend };
  }, [onNoteOn, onNoteOff, onControlChange, onPitchBend]);
  
  // Parse MIDI message
  const parseMIDIMessage = useCallback((message) => {
    const [status, data1, data2] = message.data;
    const messageType = status & 0xF0;
    const channel = status & 0x0F;
    
    return { messageType, channel, data1, data2 };
  }, []);
  
  // Handle incoming MIDI message
  const handleMIDIMessage = useCallback((event) => {
    if (!isEnabled) return;
    
    const { messageType, channel, data1, data2 } = parseMIDIMessage(event);
    const { onNoteOn, onNoteOff, onControlChange, onPitchBend } = callbacksRef.current;
    
    switch (messageType) {
      case MIDI_NOTE_ON:
        // Note on with velocity 0 is treated as note off
        if (data2 > 0) {
          onNoteOn?.(data1, data2, channel);
        } else {
          onNoteOff?.(data1, 0, channel);
        }
        break;
        
      case MIDI_NOTE_OFF:
        onNoteOff?.(data1, data2, channel);
        break;
        
      case MIDI_CONTROL_CHANGE:
        onControlChange?.(data1, data2, channel);
        break;
        
      case MIDI_PITCH_BEND:
        // Pitch bend is 14-bit: combine data1 (LSB) and data2 (MSB)
        const pitchBendValue = (data2 << 7) | data1;
        // Normalize to -1 to 1 range (8192 is center)
        const normalizedValue = (pitchBendValue - 8192) / 8192;
        onPitchBend?.(normalizedValue, channel);
        break;
        
      default:
        // Ignore other message types
        break;
    }
  }, [isEnabled, parseMIDIMessage]);
  
  // Update device lists from MIDI access
  const updateDeviceLists = useCallback((midiAccess) => {
    const inputList = [];
    const outputList = [];
    
    midiAccess.inputs.forEach((input) => {
      inputList.push({
        id: input.id,
        name: input.name || 'Unknown Device',
        manufacturer: input.manufacturer || 'Unknown',
        state: input.state,
        connection: input.connection,
      });
    });
    
    midiAccess.outputs.forEach((output) => {
      outputList.push({
        id: output.id,
        name: output.name || 'Unknown Device',
        manufacturer: output.manufacturer || 'Unknown',
        state: output.state,
        connection: output.connection,
      });
    });
    
    setInputs(inputList);
    setOutputs(outputList);
    
    // Auto-select first input if none selected
    if (inputList.length > 0 && !selectedInputId) {
      setSelectedInputId(inputList[0].id);
    }
    
    // Clear selection if device disconnected
    if (selectedInputId && !inputList.find(i => i.id === selectedInputId)) {
      setSelectedInputId(inputList.length > 0 ? inputList[0].id : null);
    }
  }, [selectedInputId]);
  
  // Handle state change (hot-plug)
  const handleStateChange = useCallback((event) => {
    if (midiAccessRef.current) {
      updateDeviceLists(midiAccessRef.current);
    }
  }, [updateDeviceLists]);
  
  // Attach message listener to selected input
  useEffect(() => {
    if (!midiAccessRef.current || !selectedInputId) return;
    
    // Remove listener from all inputs first
    midiAccessRef.current.inputs.forEach((input) => {
      input.onmidimessage = null;
    });
    
    // Attach to selected input
    const selectedInput = midiAccessRef.current.inputs.get(selectedInputId);
    if (selectedInput) {
      selectedInput.onmidimessage = handleMIDIMessage;
    }
    
    return () => {
      if (selectedInput) {
        selectedInput.onmidimessage = null;
      }
    };
  }, [selectedInputId, handleMIDIMessage]);
  
  // Request MIDI access
  const requestAccess = useCallback(async () => {
    if (!isSupported) {
      setError('Web MIDI API is not supported in this browser');
      return false;
    }
    
    if (hasAccess) return true;
    
    setIsRequesting(true);
    setError(null);
    
    try {
      const midiAccess = await navigator.requestMIDIAccess({ sysex: false });
      midiAccessRef.current = midiAccess;
      
      // Set up hot-plug handling
      midiAccess.onstatechange = handleStateChange;
      
      // Get initial device lists
      updateDeviceLists(midiAccess);
      
      setHasAccess(true);
      setIsRequesting(false);
      
      return true;
    } catch (err) {
      console.error('Failed to get MIDI access:', err);
      setError(err.message || 'Failed to access MIDI devices');
      setIsRequesting(false);
      return false;
    }
  }, [isSupported, hasAccess, handleStateChange, updateDeviceLists]);
  
  // Select a MIDI input device
  const selectInput = useCallback((inputId) => {
    setSelectedInputId(inputId);
  }, []);
  
  // Enable/disable MIDI input
  const setMIDIEnabled = useCallback((enabled) => {
    setIsEnabled(enabled);
  }, []);
  
  // Send MIDI message to output (for future use)
  const sendMIDI = useCallback((outputId, message) => {
    if (!midiAccessRef.current) return;
    
    const output = midiAccessRef.current.outputs.get(outputId);
    if (output) {
      output.send(message);
    }
  }, []);
  
  // Get currently selected input
  const getSelectedInput = useCallback(() => {
    return inputs.find(i => i.id === selectedInputId) || null;
  }, [inputs, selectedInputId]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (midiAccessRef.current) {
        midiAccessRef.current.inputs.forEach((input) => {
          input.onmidimessage = null;
        });
        midiAccessRef.current.onstatechange = null;
      }
    };
  }, []);
  
  return {
    // State
    isSupported,
    hasAccess,
    isRequesting,
    error,
    inputs,
    outputs,
    selectedInputId,
    isEnabled,
    
    // Actions
    requestAccess,
    selectInput,
    setMIDIEnabled,
    sendMIDI,
    getSelectedInput,
  };
};

export default useMIDIDevices;
